import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import StageChip from './StageChip';
import { validTransitions, stageMeta } from '../../lib/stages';
import StageTransitionDialog from '../admin/StageTransitionDialog';
import { api } from '../../lib/api';

// StageChanger — dropdown trigger on AccountDetail. Every transition (canonical
// or forced) routes through StageTransitionDialog so per-stage structured
// fields (channel, response_type, lost_reason, etc) get captured in
// `interactions.structured_payload_json`.
//
// "Force any stage…" is admin + ops (Sprint K). It opens the same dialog with
// forceMode=true — user picks any stage, the appropriate per-stage form
// renders, and an override_reason is required before submit.
export default function StageChanger({ account, role, onChanged }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // { defaultTarget, forceMode }
  const [warning, setWarning] = useState(null); // stage moved, draft invoice didn't

  const current = account?.lifecycleStage;
  const type = account?.type || 'school';
  const allowed = validTransitions(current, type);
  const isStaff = role === 'nexsav_admin' || role === 'ops';

  const submit = async (apiPayload) => {
    const res = await api.post(`/api/admin/accounts/${account.id}/stage`, {
      target: apiPayload.to_stage,
      ...apiPayload,
    });
    if (res?.account) onChanged?.(res.account);
    setWarning(res?.draft_invoice_error || null);
  };

  return (
    <>
      {warning && (
        <div role="alert" data-testid="stage-draft-invoice-warning" className="mb-2 flex items-start justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200">
          <span>{warning}</span>
          <button type="button" onClick={() => setWarning(null)} className="text-amber-300/80 hover:text-white" aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="relative inline-block">
        <button
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-white/[0.03] transition-colors"
        >
          <StageChip stage={current} enteredAt={account?.lifecycleStageEnteredAt} />
          <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute top-full left-0 mt-1 z-50 min-w-[220px] rounded-xl border border-white/[0.08] bg-[#0a0a0f] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.9)] p-1.5">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider px-2 py-1">Move to</div>
              {allowed.length === 0 && (
                <div className="px-2 py-2 text-[12px] text-gray-500">No valid transitions</div>
              )}
              {allowed.map((target) => (
                <button
                  key={target}
                  onClick={() => { setOpen(false); setPending({ defaultTarget: target, forceMode: false }); }}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg text-left text-[12px] text-gray-300 hover:bg-white/[0.04] hover:text-white"
                >
                  <span>{stageMeta(target).label}</span>
                </button>
              ))}
              {isStaff && (
                <>
                  <div className="border-t border-white/[0.05] my-1" />
                  <div className="text-[10px] text-gray-600 uppercase tracking-wider px-2 py-1">Override</div>
                  <button
                    onClick={() => { setOpen(false); setPending({ defaultTarget: '', forceMode: true }); }}
                    data-testid="stage-changer-force-open"
                    className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-amber-300 hover:bg-amber-500/10"
                  >
                    Force any stage…
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
      {pending && (
        <StageTransitionDialog
          open
          account={account}
          forceMode={pending.forceMode}
          defaultTarget={pending.defaultTarget}
          onClose={() => setPending(null)}
          onConfirm={async (apiPayload) => {
            await submit(apiPayload);
            setPending(null);
          }}
        />
      )}
    </>
  );
}
